import {
  AccessoryAction,
  accessoryGroups,
  AccessoryState,
} from "@/lib/helpers.ts";
import { Dispatch, useMemo, useCallback } from "react";
import { useDisclosure } from "@mantine/hooks";
import {
  Accordion,
  Avatar,
  Button,
  CloseButton,
  Group,
  InputBase,
  Modal,
  Paper,
  Pill,
  Text,
} from "@mantine/core";
import { AnglesUpDown } from "@/icons/AnglesUpDown";
import { AccessoriesCard } from "@/ui/shared/AccessoriesCard";
import { AccessoriesPill } from "@/ui/shared/AccessoriesPill";

interface Props {
  accessories: AccessoryState;
  setAccessories: Dispatch<AccessoryAction>;
}

export function AccessoriesSelect({ accessories, setAccessories }: Props) {
  const [opened, { open, close }] = useDisclosure(false);

  // Memoize setAccessory to prevent recreation on every render
  const setAccessory = useCallback(
    (name: string, value: string | null) => {
      setAccessories({ type: "SET_ACCESSORY", payload: { name, value } });
    },
    [setAccessories],
  );

  // Memoize resetAll to prevent recreation on every render
  const resetAll = useCallback(() => {
    setAccessories({ type: "RESET_ALL" });
  }, [setAccessories]);

  const hasValues = Object.values(accessories).some((value) => value !== null);

  const groupsComponent = useMemo(
    () =>
      accessoryGroups.map((group) => (
        <Accordion.Item key={group.handle} value={group.handle}>
          <Accordion.Control>
            <Group wrap="nowrap">
              <Avatar src={group.image} radius="xl" size="lg" />
              <div>
                <Text>{group.title}</Text>
                <Text size="sm" c="dimmed" fw={400}>
                  {group.description}
                </Text>
              </div>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <div className="flex flex-wrap justify-center md:justify-start gap-3">
              {group.panels.map((panel) => (
                <AccessoriesCard
                  key={panel.group}
                  value={accessories[panel.group]}
                  setValue={(value) => setAccessory(panel.group, value)} // Pass the memoized setAccessory
                  {...panel}
                />
              ))}
            </div>
          </Accordion.Panel>
        </Accordion.Item>
      )),
    [accessories, setAccessory],
  );

  const handleClose = useCallback(() => {
    close();
  }, [close]);

  const modalStyles = useMemo(() => ({ body: { paddingBottom: 0 } }), []);

  const memoizedModal = useMemo(
    () => (
      <Modal
        styles={modalStyles}
        fullScreen
        opened={opened}
        onClose={close}
        title="Clothing and accessories"
        className="relative"
      >
        <Accordion variant="contained" defaultValue="body-clothes">
          {groupsComponent}
        </Accordion>

        <Paper p="lg" radius={0} className="sticky bottom-0 mt-5 z-30">
          <div className="flex justify-center">
            <Button
              onClick={handleClose}
              variant="default"
              className="flex items-center gap-2 bg-transparent p-1 text-xs"
            >
              Close
            </Button>
          </div>
        </Paper>
      </Modal>
    ),
    [opened, close, groupsComponent, handleClose, modalStyles],
  );

  const memoizedRightSection = useMemo(
    () => (hasValues ? <CloseButton onClick={resetAll} /> : <AnglesUpDown />),
    [hasValues, resetAll],
  );

  // Memoize the Pill.Group to prevent unnecessary re-renders
  const memoizedPills = useMemo(
    () => (
      <Pill.Group>
        {Object.entries(accessories).map(([name, value]) => (
          <AccessoriesPill
            key={name}
            value={value}
            onRemove={() => {
              setAccessories({
                type: "SET_ACCESSORY",
                payload: { name, value: null },
              });
            }}
          />
        ))}
      </Pill.Group>
    ),
    [accessories],
  );

  return (
    <>
      <InputBase
        styles={{ input: { display: "flex", cursor: "pointer" } }}
        label="Clothing and accessories"
        onClick={open}
        size="md"
        component="div"
        multiline
        rightSection={memoizedRightSection}
      >
        {memoizedPills}
      </InputBase>

      {memoizedModal}
    </>
  );
}
